import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';

import { getDivisions, loadDivisions } from '../../../store/divisions';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import Button from '../../themed/button';
import Conditional from '../../common/conditional';
import EditDivision from '../../forms/editDivision';
import Loader from '../../themed/loader';
import NoItems from '../../blocks/no-items';
import Table from '../../common/table';
import Input from '../../themed/input/input';
import Pagination from '../../themed/pagination';
import Select from '../../themed/select/select';
import Modal from '../../themed/modal';
import SyncTime from '../../blocks/sync-time';

import * as utils from '../../../utils/utilities';
import * as models from '../../../utils/models';

const DivisionsList: React.FC = (): JSX.Element => {
	const [sortColumn, setSortColumn] = useState(initialSortColumn);
	const [searchQuery, setSearchQuery] = useState('');
	const [pageSize, setPageSize] = useState(10);
	const [currentPage, setCurrentPage] = useState(1);
	const [searchColumn, setSearchColumn] = useState('divisionCode');
	const [selectedDivision, setSelectedDivision] = useState<models.Division | null>();

	const dispatch = useAppDispatch();
	const divisions = useAppSelector(getDivisions);

	const filtered = useMemo((): models.Division[] => {
		const defaultSearchColumn = searchColumn || 'divisionName';

		return divisions.list.filter((division) => _.get(division, defaultSearchColumn).toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1);
	}, [searchColumn, searchQuery, divisions.list]);

	const paginatedData = useMemo(() => {
		const sorted = _.orderBy(filtered, [sortColumn.path], [sortColumn.order]);
		const paginated = utils.paginate(sorted, currentPage, pageSize);

		return { totalCount: filtered.length, paginated };
	}, [filtered, sortColumn, currentPage, pageSize]);

	const closeSelectedDivision = (): void => {
		setSelectedDivision(null);
	};

	const getTableColumns = (): models.Column[] => {
		const deleteColumn: models.Column = {
			path: 'id',
			label: '',
			content: (d: models.Division) => (
				<Button
					icon="pencil-fill"
					onClick={() => setSelectedDivision(d)}
					label=""
				/>
			),
		};

		return [...columns, deleteColumn];
	};

	const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const pageSize: number = +e.target.value;
		const actualPageSize: number = pageSize === 0 ? 1 : pageSize;

		setPageSize(actualPageSize);
	};

	const handleSearchKeyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSearchColumn(e.target.value);
	};

	const handleSetSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value);
	};

	const refreshDivisions = useCallback((): void => {
		dispatch(loadDivisions());
	}, [dispatch]);

	useEffect(() => {
		refreshDivisions();
	}, [refreshDivisions]);

	return (
		<React.Fragment>
			<Modal isVisible={!!selectedDivision} onClose={closeSelectedDivision}>
				<EditDivision selectedDivision={selectedDivision} />
			</Modal>

			<h2 className="form-title">Manage Divisions</h2>

			<div className="divider my-3"></div>

			<div className="d-flex justify-content-between mb-2">
				<div className="d-flex justify-content-center">
					<Select
						name="searchColumn"
						placeholder="Search By"
						options={searchColumns}
						value={searchColumn}
						onChange={handleSearchKeyChange}
					/>

					<div className="mx-4">
						<Input
							name="search"
							value={searchQuery}
							onChange={handleSetSearch}
							placeholder={`Enter search value`}
						/>
					</div>
				</div>

				<SyncTime lastSynced={divisions.lastUpdated} />
			</div>

			<Loader isVisible={divisions.loading} />

			<Conditional isVisible={divisions.list.length > 0}>
				<Table
					columns={getTableColumns()}
					data={paginatedData.paginated}
					sortColumn={sortColumn}
					onSort={setSortColumn}
					isFetching={false}
				/>
			</Conditional>

			<NoItems
				isVisible={divisions.list.length === 0}
				onRefresh={refreshDivisions}
			/>

			<div className="d-flex justify-content-between align-items-center mt-4">
				<Select
					name="page-size"
					placeholder={'Showing (' + pageSize + 'rows)'}
					options={utils.transformPageRange(20)}
					value={pageSize}
					onChange={handlePageSizeChange}
				/>

				<Pagination
					itemsCount={paginatedData.totalCount}
					pageSize={pageSize}
					currentPage={currentPage}
					onPageChange={(page: number) => setCurrentPage(page)}
				/>
			</div>
		</React.Fragment>
	);
};

const initialSortColumn: models.SortColumn = {
	path: 'name',
	order: 'asc',
};

const columns: models.Column[] = [
	{
		path: 'divisionName',
		label: 'Division Name',
	},
	{
		path: 'divisionCode',
		label: 'Division Code',
	},
	{
		path: 'inspectionType',
		label: 'Permitted Inspection Types',
		content: (division: models.Division) => (
			<span>
				{division.inspectionType.map((t) => t.inspectionName).join(',')}
			</span>
		),
	},
];

const searchColumns: models.SelectOption[] = [
	{
		id: 'divisionCode',
		name: 'Division Code',
	},
	{
		id: 'divisionName',
		name: 'Division Name',
	},
];

export default DivisionsList;
